import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { matchingAPI } from '../api/api';
import MatchScoreBadge, { MatchScoreBreakdown } from './MatchScoreBadge';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Users, MapPin, DollarSign, Star, ChevronDown, ChevronUp,
  RefreshCw, MessageSquare, Eye, Filter, X
} from 'lucide-react';

const MatchingWorkersModal = ({ jobId, jobTitle, isOpen, onClose }) => {
  const navigate = useNavigate();
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [minScore, setMinScore] = useState(0);
  const [expandedWorker, setExpandedWorker] = useState(null);

  useEffect(() => {
    if (isOpen && jobId) {
      fetchMatchingWorkers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, [isOpen, jobId, minScore]);

  const fetchMatchingWorkers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await matchingAPI.getWorkersForJob(jobId, { min_score: minScore, limit: 20 });
      setWorkers(response.data.workers || []);
    } catch (err) {
      console.error('Error fetching matching workers:', err);
      setError('Unable to load matching workers');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="matching-workers-modal"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Users className="h-6 w-6" />
                Find Matching Workers
              </h2>
              <p className="text-blue-100 text-sm mt-1">For: {jobTitle}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          {/* Filter */}
          <div className="mt-4 flex items-center gap-4">
            <Filter className="h-4 w-4 text-blue-200" />
            <div className="flex-1">
              <label className="text-xs text-blue-200">
                Min Score: <span className="font-bold text-white">{minScore}%</span>
              </label>
              <input
                type="range"
                min="0"
                max="80"
                step="10"
                value={minScore}
                onChange={(e) => setMinScore(Number(e.target.value))}
                className="w-full mt-1 accent-white"
                data-testid="worker-min-score-filter"
              />
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchMatchingWorkers}
              className="text-white hover:bg-white/20"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(85vh-180px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
              <span className="ml-3 text-gray-500">Finding best matches...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-gray-500">{error}</p>
              <Button variant="outline" size="sm" onClick={fetchMatchingWorkers} className="mt-4">
                Try Again
              </Button>
            </div>
          ) : workers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No matching workers found.</p>
              <p className="text-sm text-gray-400 mt-1">Try lowering the minimum score filter.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                Found <span className="font-semibold text-blue-600">{workers.length}</span> matching workers
              </p>
              
              {workers.map((worker) => (
                <div
                  key={worker.worker_id}
                  className={`p-4 rounded-xl border transition-all hover:shadow-md ${
                    worker.match_level === 'excellent' ? 'border-green-200 bg-green-50/50' :
                    worker.match_level === 'good' ? 'border-blue-200 bg-blue-50/50' :
                    'border-gray-200 bg-white'
                  }`}
                  data-testid={`matching-worker-${worker.worker_id}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Avatar */}
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                      {worker.name?.charAt(0) || '?'}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-gray-900">{worker.name}</h4>
                        <MatchScoreBadge score={worker.match_score} size="sm" />
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 mb-2">
                        {worker.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {worker.location}
                          </span>
                        )}
                        {worker.hourly_rate > 0 && (
                          <span className="flex items-center gap-1">
                            <DollarSign className="h-3 w-3" />
                            ${worker.hourly_rate}/hr
                          </span>
                        )}
                        {worker.breakdown?.rating?.avg_rating > 0 && (
                          <span className="flex items-center gap-1">
                            <Star className="h-3 w-3 text-amber-500" />
                            {worker.breakdown.rating.avg_rating.toFixed(1)}
                          </span>
                        )}
                      </div>
                      
                      {/* Skills */}
                      {worker.skills?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {worker.skills.slice(0, 5).map((skill, idx) => (
                            <Badge key={idx} variant="secondary" className="text-[10px] bg-blue-100 text-blue-700">
                              {skill}
                            </Badge>
                          ))}
                          {worker.skills.length > 5 && (
                            <Badge variant="secondary" className="text-[10px] bg-gray-100">
                              +{worker.skills.length - 5}
                            </Badge>
                          )}
                        </div>
                      )}
                      
                      {/* Bio */}
                      {worker.bio && (
                        <p className="text-sm text-gray-600 line-clamp-2">{worker.bio}</p>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedWorker(expandedWorker === worker.worker_id ? null : worker.worker_id)}
                        className="text-gray-400"
                      >
                        {expandedWorker === worker.worker_id ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/worker/${worker.worker_id}`)}
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        Profile
                      </Button>
                      <Button
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700"
                        onClick={() => {
                          onClose();
                          navigate(`/messages?partner=${worker.worker_id}`);
                        }}
                        data-testid={`contact-worker-${worker.worker_id}`}
                      >
                        <MessageSquare className="h-3 w-3 mr-1" />
                        Contact
                      </Button>
                    </div>
                  </div>
                  
                  {/* Expanded breakdown */}
                  {expandedWorker === worker.worker_id && worker.breakdown && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <MatchScoreBreakdown breakdown={worker.breakdown} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MatchingWorkersModal;
